import { ref } from 'vue'
import { getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage'
import { getApp } from 'firebase/app'
import { lessonThumbnailPath } from '../stores/TransformLearningStore'
import { handleErrors } from '../utils/errorHandler'

/**
 * Resolves Firebase Storage thumbnail URLs for Transform lessons. Any lesson type
 * (video or quiz) may have a stored thumbnail; lessons without one fall back to the
 * placeholder in the card. A missing thumbnail is non-fatal: it is logged (not sent to
 * Sentry — it is expected when Storage rules are inactive locally) and the caller shows
 * the placeholder.
 *
 * @param {string} logScope - prefix for handleErrors keys (usually the view name).
 * @returns {{ thumbnailUrls, resolveThumbnail, resolveThumbnails }}
 */
// Module-scoped (singleton) cache of lessonId → resolved Storage URL, mirroring
// useAgencyBranding: a Storage download URL is stable for the session, so resolving it
// once and sharing it across every view/mount avoids the per-navigation re-resolution
// (a getDownloadURL round-trip per thumbnail) that made lists feel slow.
const thumbnailUrls = ref({})

export function useLessonThumbnails(logScope = 'useLessonThumbnails') {
  // Resolves a single lesson's thumbnail (no-op if already-resolved or missing data).
  // Returns the URL or null.
  async function resolveThumbnail(lesson) {
    if (!lesson || !lesson.thumbnailFileName || !lesson.courseId) {
      return null
    }
    if (thumbnailUrls.value[lesson.id]) return thumbnailUrls.value[lesson.id]
    try {
      const url = await getDownloadURL(storageRef(getStorage(getApp()), lessonThumbnailPath(lesson)))
      thumbnailUrls.value = { ...thumbnailUrls.value, [lesson.id]: url }
      return url
    } catch (err) {
      handleErrors(`${logScope}.resolveThumbnail-${lesson.id}`, err, { sendToSentry: false })
      return null
    }
  }

  // Batch-resolves thumbnails for a list of lessons (in parallel).
  async function resolveThumbnails(lessons = []) {
    await Promise.all(lessons.map(l => resolveThumbnail(l)))
  }

  // Clears the singleton cache. Must run on logout (alongside the store reset) so a
  // subsequent user never inherits the previous user's resolved Storage URLs and the
  // map cannot grow without bound across sessions.
  function resetThumbnails() {
    thumbnailUrls.value = {}
  }

  return { thumbnailUrls, resolveThumbnail, resolveThumbnails, resetThumbnails }
}
